import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Webhook, Plus, Trash2, Play, Pause, AlertCircle, CheckCircle, Activity, Copy, Zap, Info, ArrowLeft } from 'lucide-react';
import { webhookService } from '@/services/webhookService';
import { useGlobalToast } from '@/contexts/ToastContext';
import { Breadcrumb } from '@/components/ui/Breadcrumb';
import { Button } from '@/components/ui/Button';
import { Card } from '@/components/ui/Card';
import { Badge } from '@/components/ui/Badge';

export function WebhookManagementPage() {
  const navigate = useNavigate();
  const { success, error } = useGlobalToast();
  const [webhooks, setWebhooks] = useState<any[]>([]);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [selectedWebhook, setSelectedWebhook] = useState<any>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    loadWebhooks();
  }, []);

  const loadWebhooks = async () => {
    setIsLoading(true);
    try {
      const webhookList = webhookService.getWebhooks();
      const transformedWebhooks = webhookList.map(webhook => ({
        ...webhook,
        name: `Webhook ${webhook.id.slice(0, 8)}`,
        status: webhook.isActive ? 'Active' : 'Inactive',
        successRate: webhook.successCount + webhook.failureCount > 0
          ? Math.round((webhook.successCount / (webhook.successCount + webhook.failureCount)) * 100)
          : 100,
        lastDelivery: webhook.lastTriggered,
        totalDeliveries: webhook.successCount + webhook.failureCount,
        failedDeliveries: webhook.failureCount,
        recentDeliveries: [] // Mock or fetched
      }));
      setWebhooks(transformedWebhooks);
    } catch (err) {
      error('Failed to load webhooks', 'Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleCreateWebhook = async (webhookData: any) => {
    try {
      await webhookService.registerWebhook(
        webhookData.url,
        webhookData.events,
        webhookData.secret,
        {}
      );
      success('Webhook Created', 'New webhook endpoint registered successfully.');
      setShowCreateForm(false);
      loadWebhooks();
    } catch (err) {
      error('Creation Failed', 'Could not register webhook.');
    }
  };

  const handleToggleWebhook = async (webhookId: string, enabled: boolean) => {
    try {
      await webhookService.updateWebhook(webhookId, { isActive: enabled });
      success('Webhook Updated', `Webhook ${enabled ? 'enabled' : 'disabled'}.`);
      loadWebhooks();
    } catch (err) {
      error('Update Failed', 'Could not update webhook status.');
    }
  };

  const handleDeleteWebhook = async (webhookId: string) => {
    if (!confirm('Are you sure you want to delete this webhook?')) return;

    try {
      await webhookService.deleteWebhook(webhookId);
      success('Webhook Deleted', 'Webhook endpoint removed.');
      loadWebhooks();
    } catch (err) {
      error('Delete Failed', 'Could not delete webhook.');
    }
  };

  const handleTestWebhook = async (_webhookId: string) => {
    try {
      await webhookService.triggerEvent('job.created', {
        id: 'test_job_123',
        title: 'Test Event',
        description: 'This is a test event payload.'
      });
      success('Test Event Sent', 'A test payload has been sent to the endpoint.');
    } catch (err) {
      error('Test Failed', 'Could not send test event.');
    }
  };

  const breadcrumbItems = [
    { label: 'Admin', href: '/admin' },
    { label: 'Webhooks' }
  ];

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-6">
        <div className="text-center">
          <div className="w-8 h-8 border-4 border-primary-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-500 text-sm">Loading webhooks...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 text-gray-900 pb-20">
      <a href="#main-content" className="sr-only focus:not-sr-only focus:absolute focus:top-4 focus:left-4 focus:z-[100] focus:px-4 focus:py-2 focus:bg-primary-600 focus:text-white focus:rounded-lg focus:shadow-lg min-h-touch">
        Skip to main content
      </a>
      <main id="main-content" role="main" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8">
        <div className="flex items-center justify-between mb-8">
          <Breadcrumb items={breadcrumbItems} onBack={() => navigate('/admin')} />
          <Button variant="primary" onClick={() => setShowCreateForm(true)} icon={<Plus className="w-4 h-4" />}>Add Endpoint</Button>
        </div>

        <div className="mb-6">
          <h1 className="text-3xl font-bold text-gray-900">Developer Settings</h1>
          <p className="text-gray-600 mt-1">Manage webhooks and event subscriptions.</p>
        </div>

        <div className="grid gap-6">
          {webhooks.length === 0 ? (
            <Card className="py-16 text-center">
              <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Webhook className="w-8 h-8 text-gray-400" />
              </div>
              <h3 className="text-lg font-medium text-gray-900 mb-2">No Webhooks Configured</h3>
              <p className="text-gray-500 mb-6 max-w-md mx-auto">Create a webhook to subscribe to real-time events from the platform.</p>
              <Button variant="primary" onClick={() => setShowCreateForm(true)}>Add First Webhook</Button>
            </Card>
          ) : (
            webhooks.map((webhook) => (
              <Card key={webhook.id} className="overflow-hidden">
                <div className="p-6 border-b border-gray-100 flex flex-col md:flex-row md:items-center justify-between gap-4">
                  <div className="flex items-start gap-4">
                    <div className={`p-3 rounded-lg ${webhook.isActive ? 'bg-emerald-50 text-emerald-600' : 'bg-gray-100 text-gray-500'}`}>
                      <Webhook className="w-6 h-6" />
                    </div>
                    <div>
                      <div className="flex items-center gap-3">
                        <h3 className="text-lg font-bold text-gray-900">{webhook.url}</h3>
                        <Badge variant={webhook.isActive ? 'success' : 'secondary'}>{webhook.status}</Badge>
                      </div>
                      <div className="flex flex-wrap gap-2 mt-2">
                        {webhook.events.map((event: string) => (
                          <span key={event} className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-800 border border-gray-200">
                            {event}
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>

                  <div className="flex items-center gap-2 self-start md:self-center">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleTestWebhook(webhook.id)}
                      icon={<Zap className="w-4 h-4" />}
                    >
                      Test
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleToggleWebhook(webhook.id, !webhook.isActive)}
                      icon={webhook.isActive ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4" />}
                    >
                      {webhook.isActive ? 'Pause' : 'Resume'}
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="text-red-600 hover:text-red-700 hover:bg-red-50"
                      onClick={() => handleDeleteWebhook(webhook.id)}
                      icon={<Trash2 className="w-4 h-4" />}
                    >
                      Delete
                    </Button>
                  </div>
                </div>

                <div className="bg-gray-50/50 p-4 grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                  <div>
                    <span className="block text-gray-500 text-xs uppercase font-semibold">Success Rate</span>
                    <span className={`font-mono font-medium ${webhook.successRate > 90 ? 'text-emerald-600' : 'text-amber-600'}`}>{webhook.successRate}%</span>
                  </div>
                  <div>
                    <span className="block text-gray-500 text-xs uppercase font-semibold">Total Deliveries</span>
                    <span className="font-mono font-medium text-gray-900">{webhook.totalDeliveries}</span>
                  </div>
                  <div>
                    <span className="block text-gray-500 text-xs uppercase font-semibold">Failures</span>
                    <span className="font-mono font-medium text-gray-900">{webhook.failedDeliveries}</span>
                  </div>
                  <div>
                    <span className="block text-gray-500 text-xs uppercase font-semibold">Last Delivery</span>
                    <span className="font-mono font-medium text-gray-900">{webhook.lastDelivery ? new Date(webhook.lastDelivery).toLocaleString() : 'Never'}</span>
                  </div>
                </div>
              </Card>
            ))
          )}
        </div>

        {showCreateForm && (
          <div className="fixed inset-0 bg-gray-900/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
            <div className="bg-white rounded-xl shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto animate-in zoom-in-95 duration-200">
              <div className="px-6 py-4 border-b border-gray-100 flex justify-between items-center">
                <h3 className="text-lg font-bold text-gray-900">Add Webhook Endpoint</h3>
                <button onClick={() => setShowCreateForm(false)} className="p-2 text-gray-400 hover:text-gray-600 rounded-full hover:bg-gray-100">
                  <ArrowLeft className="w-5 h-5" />
                </button>
              </div>
              <WebhookCreateForm
                onSubmit={handleCreateWebhook}
                onCancel={() => setShowCreateForm(false)}
              />
            </div>
          </div>
        )}
      </main>
    </div>
  );
}

function WebhookCreateForm({ onSubmit, onCancel }: { onSubmit: (data: any) => void; onCancel: () => void }) {
  const [formData, setFormData] = useState({
    url: '',
    events: [] as string[],
    secret: '',
  });

  const availableEvents = [
    'job.created', 'job.updated', 'job.completed', 'job.cancelled',
    'payment.processed', 'payment.failed', 'user.registered',
    'user.verified', 'provider.verified', 'review.created'
  ];

  const handleEventToggle = (event: string) => {
    setFormData(prev => ({
      ...prev,
      events: prev.events.includes(event)
        ? prev.events.filter(e => e !== event)
        : [...prev.events, event]
    }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSubmit(formData);
  };

  return (
    <form onSubmit={handleSubmit} className="p-6 space-y-6">
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Endpoint URL</label>
        <div className="relative">
          <input
            type="url"
            required
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none text-sm pl-10"
            placeholder="https://api.your-domain.com/webhooks"
            value={formData.url}
            onChange={(e) => setFormData(prev => ({ ...prev, url: e.target.value }))}
          />
          <Activity className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">Events to Subscribe</label>
        <div className="grid grid-cols-2 gap-2 max-h-48 overflow-y-auto border border-gray-200 rounded-lg p-3 bg-gray-50">
          {availableEvents.map((event) => (
            <label key={event} className="flex items-center gap-2 cursor-pointer p-2 hover:bg-white rounded transition-colors">
              <input
                type="checkbox"
                checked={formData.events.includes(event)}
                onChange={() => handleEventToggle(event)}
                className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
              />
              <span className="text-sm text-gray-700">{event}</span>
            </label>
          ))}
        </div>
        <p className="text-xs text-gray-500 mt-2">Select at least one event to trigger this webhook.</p>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Secret Key (Optional)</label>
        <input
          type="text"
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none text-sm font-mono"
          placeholder="whsec_..."
          value={formData.secret}
          onChange={(e) => setFormData(prev => ({ ...prev, secret: e.target.value }))}
        />
        <p className="text-xs text-gray-500 mt-1">Used to sign webhook payloads for verification.</p>
      </div>

      <div className="flex justify-end gap-3 pt-4 border-t border-gray-100">
        <Button type="button" variant="ghost" onClick={onCancel}>Cancel</Button>
        <Button type="submit" variant="primary" disabled={!formData.url || formData.events.length === 0}>Create Webhook</Button>
      </div>
    </form>
  );
}
